import { AbsoluteFill, useCurrentFrame, interpolate, Sequence, Audio, spring, Img } from 'remotion';
import { z } from 'zod';
import { TransitionSeries, linearTiming } from '@remotion/transitions';
import { fade } from '@remotion/transitions/fade';
import React from 'react';

// Define a valid schema that Remotion can use
export const composition0002Schema = z
  .object({
    images: z
      .array(
        z.object({
          url: z.string(),
          zoomDirection: z.enum(['in', 'out']).optional(),
          panDirection: z.enum(['right', 'left', 'up', 'down']).optional(),
          text: z.string().optional(),
          textBackgroundColor: z.string().optional(),
        })
      )
      .optional(),
    musicUrl: z.string().optional(),
    framesPerImage: z.number().optional(),
    secondsPerImage: z.number().optional(),
  })
  .passthrough();

// Calculate total duration for images with transitions
const FADE_DURATION = 30; // 1 second fade at 30fps
const DEFAULT_FRAMES_PER_IMAGE = 150;

// Helper: Calculate total frames for all slides including transitions
function getMainSequenceDuration(imagesLength: number, framesPerImage: number) {
  if (imagesLength === 0) return 0;
  // Each transition overlaps the end of one slide and the start of the next
  // So total = first image + (n-1) * (framesPerImage - FADE_DURATION)
  return framesPerImage + (imagesLength - 1) * (framesPerImage - FADE_DURATION);
}

// Shared function to calculate duration from props
export function calculateDurationFromProps(props: z.infer<typeof composition0002Schema>) {
  const images = props.images || [];
  let framesPerImage = props.framesPerImage;
  if (props.secondsPerImage) {
    framesPerImage = props.secondsPerImage * 30; // 30fps
  }
  framesPerImage = framesPerImage || DEFAULT_FRAMES_PER_IMAGE;

  const mainSequenceFrames = getMainSequenceDuration(images.length, framesPerImage);

  return {
    mainSequenceFrames,
    totalFrames: mainSequenceFrames,
    framesPerImage,
  };
}

// Export calculateMetadata for Remotion
export const calculateMetadata = ({ props }: { props: z.infer<typeof composition0002Schema> }) => {
  const { totalFrames } = calculateDurationFromProps(props);
  return {
    durationInFrames: totalFrames,
  };
};

// Helper function to determine if we should use black text
function shouldUseBlackText(backgroundColor: string) {
  // Convert hex to RGB
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance using sRGB coefficients
  // Using the formula from WCAG 2.0
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return true for light colors (luminance > 0.6)
  return luminance > 0.6;
}

function KenBurnsImage({ imageUrl, zoomDirection = 'in', panDirection = 'right', framesPerImage = 150 }: any) {
  const frame = useCurrentFrame();
  const progress = interpolate(frame, [0, framesPerImage], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Calculate zoom effect
  const zoomStart = zoomDirection === 'in' ? 1 : 1.2;
  const zoomEnd = zoomDirection === 'in' ? 1.2 : 1;
  const zoom = interpolate(progress, [0, 1], [zoomStart, zoomEnd]);

  // Calculate the maximum allowed pan based on the zoom level
  // This ensures the image always covers the entire viewport
  // For a zoom level of z, the maximum pan is (z-1)/z
  // This formula ensures the image edge never reaches the viewport edge
  const maxPanAmount = (zoom - 1) / zoom;

  // Apply a scaling factor to make the movement more subtle
  // This can be adjusted to control the intensity of the pan effect
  const panScaleFactor = 0.3; // 30% of the maximum allowed pan
  const effectivePanAmount = maxPanAmount * panScaleFactor;

  let translateX = 0;
  let translateY = 0;

  if (panDirection === 'right') {
    translateX = interpolate(progress, [0, 1], [0, effectivePanAmount]);
  } else if (panDirection === 'left') {
    translateX = interpolate(progress, [0, 1], [0, -effectivePanAmount]);
  } else if (panDirection === 'up') {
    translateY = interpolate(progress, [0, 1], [0, -effectivePanAmount]);
  } else if (panDirection === 'down') {
    translateY = interpolate(progress, [0, 1], [0, effectivePanAmount]);
  }

  return (
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          transform: `scale(${zoom}) translate(${translateX * 100}%, ${translateY * 100}%)`,
        }}
      >
        <Img
          src={imageUrl}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
    </div>
  );
}

function SlideText({ text, backgroundColor = '#000000', framesPerImage = 150 }: any) {
  const frame = useCurrentFrame();
  const isLightBackground = shouldUseBlackText(backgroundColor);

  // Calculate when this text should start exiting
  const exitFrame = framesPerImage - FADE_DURATION;
  const isExiting = frame >= exitFrame;

  // Spring animation for entry and exit
  const springValue = spring({
    frame: Math.max(0, frame - FADE_DURATION),
    fps: 30,
    config: {
      damping: 200,
      mass: 0.5,
      stiffness: 200,
    },
  });

  // Exit spring animation
  const exitSpringValue = spring({
    frame: Math.max(0, frame - exitFrame),
    fps: 30,
    config: {
      damping: 160,
      mass: 0.6,
      stiffness: 180,
    },
  });

  // Calculate the y position with spring animation
  // Increased the starting position from 100 to 200 to make text travel further
  const entryYPosition = interpolate(springValue, [0, 1], [400, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Calculate entry rotation - start with slight upward rotation
  const entryRotation = interpolate(
    springValue,
    [0, 1],
    [25, 0], // Increased from 10 to 25 degrees for more dramatic entry rotation
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Calculate exit position - move down and slightly rotate
  // Increased the exit distance from 150 to 200 to make text travel further
  const exitYPosition = interpolate(exitSpringValue, [0, 1], [0, 600], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const exitRotation = interpolate(exitSpringValue, [0, 1], [0, -25], {
    // Increased from -10 to -25 degrees for more dramatic exit rotation
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  // Fade in the text after the transition, fade out when exiting
  const opacity = isExiting
    ? interpolate(frame, [exitFrame, exitFrame + 15], [1, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
    : interpolate(frame - FADE_DURATION, [0, 15], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  if (!text) return null;

  // Calculate final position and rotation
  const yPosition = isExiting ? exitYPosition : entryYPosition;
  const rotation = isExiting ? exitRotation : entryRotation;

  return (
    <div
      style={{
        position: 'absolute',
        left: '50%',
        bottom: '35%',
        transform: `translate(-50%, ${yPosition}%) rotate(${rotation}deg)`,
        opacity,
        zIndex: 10,
        width: '100%',
        padding: '0 40px',
        display: 'flex',
        justifyContent: 'center',
        transformOrigin: 'center center',
      }}
    >
      <div
        style={{
          backgroundColor,
          color: isLightBackground ? '#000000' : 'white',
          fontSize: '64px',
          fontWeight: 'bold',
          padding: '20px 40px',
          borderRadius: '16px',
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '90%',
          lineHeight: 1.2,
          letterSpacing: '-0.02em',
        }}
      >
        {text}
      </div>
    </div>
  );
}

export function Composition0002(props: any) {
  const {
    images = [
      {
        url: 'https://arthelper-staging-public-cvzenzdr.s3.amazonaws.com/2147492312/optimized/260.webp',
        zoomDirection: 'in',
        panDirection: 'right',
        text: 'Beautiful Artwork',
        textBackgroundColor: '#000000',
      },
      {
        url: 'https://ghow5ej4buyjk2mvir7oav2vxy0uzyqn.lambda-url.us-east-1.on.aws/2147492312/96zzsjnuqowpf/260',
        zoomDirection: 'out',
        panDirection: 'left',
        text: 'Stunning Creation',
        textBackgroundColor: '#000000',
      },
      {
        url: 'https://ghow5ej4buyjk2mvir7oav2vxy0uzyqn.lambda-url.us-east-1.on.aws/2147492312/x8vcegetk7j7m/260',
        zoomDirection: 'in',
        panDirection: 'up',
        text: 'Incredible Design',
        textBackgroundColor: '#000000',
      },
    ],
    musicUrl,
  } = props;

  const { mainSequenceFrames, framesPerImage } = calculateDurationFromProps(props);

  // Fade in/out for music
  const frame = useCurrentFrame();
  const fadeInFrames = 30; // 0.8s
  const fadeOutFrames = 30; // 1.2s
  const musicVolume = interpolate(
    frame,
    [0, fadeInFrames, mainSequenceFrames - fadeOutFrames, mainSequenceFrames],
    [0, 0.7, 0.7, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: 'black',
        overflow: 'hidden',
      }}
    >
      {musicUrl && <Audio src={musicUrl} volume={musicVolume} endAt={mainSequenceFrames} />}

      <AbsoluteFill>
        <Sequence durationInFrames={mainSequenceFrames}>
          <TransitionSeries>
            {images.map((image: any, index: any) => (
              <React.Fragment key={`slide-${index}`}>
                <TransitionSeries.Sequence key={index} durationInFrames={framesPerImage}>
                  <KenBurnsImage
                    imageUrl={image.url}
                    zoomDirection={image.zoomDirection}
                    panDirection={image.panDirection}
                    framesPerImage={framesPerImage}
                  />
                  <SlideText
                    text={image.text}
                    backgroundColor={image.textBackgroundColor}
                    framesPerImage={framesPerImage}
                    isLastFrame={index === images.length - 1}
                  />
                </TransitionSeries.Sequence>
                {index < images.length - 1 && (
                  <TransitionSeries.Transition
                    key={`transition-${index}`}
                    presentation={fade()}
                    timing={linearTiming({ durationInFrames: FADE_DURATION })}
                  />
                )}
              </React.Fragment>
            ))}
          </TransitionSeries>
        </Sequence>
      </AbsoluteFill>
    </AbsoluteFill>
  );
}
